import { ArrowUpRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import Placeholder from "@/components/Placeholder";

const REASONS = [
  { emoji: "🏆", title: "Hands-on leadership experience", desc: "Step into roles, lead real initiatives, and grow through the work — not just the theory." },
  { emoji: "🌱", title: "Meaningful impact", desc: "Be part of projects that reach real people and real communities across and beyond Mumbai." },
  { emoji: "🔥", title: "A strong community", desc: "Friendships, mentors, and collaborators who make the journey worth it." },
  { emoji: "⚡", title: "Skill-building opportunities", desc: "Sessions, workshops, and projects that sharpen what you already have and teach you what you don't." },
  { emoji: "🌈", title: "An inclusive environment", desc: "A space where different voices, backgrounds, and ideas belong — and matter." },
];

export default function Join() {
  return (
    <div className="pt-32 md:pt-40">
      {/* HEADER */}
      <section className="pb-20 md:pb-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
            <Reveal className="md:col-span-8">
              <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-6">
                <span className="w-8 h-px bg-[#E76F2E] inline-block align-middle mr-3" /> Become a Leo
              </div>
              <h1 className="font-playfair text-5xl md:text-7xl lg:text-[7.5rem] font-semibold text-[#2C2C2C] leading-[0.95] tracking-tight">
                Join the <em className="text-[#8B1E2D]">Leo Club</em>.
              </h1>
            </Reveal>
            <Reveal delay={0.15} className="md:col-span-4">
              <p className="font-inter text-[#555555] text-lg md:text-xl leading-relaxed">
                A space where you can contribute, learn, and grow while being part of something meaningful — wherever in the world you are.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY JOIN */}
      <section className="py-20 md:py-28 bg-[#F7F3EE]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4 md:sticky md:top-32 self-start">
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-5">
              <span className="w-8 h-px bg-[#E76F2E] inline-block align-middle mr-3" /> Why join
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl font-semibold text-[#2C2C2C] leading-[1.05]">
              What you <em className="text-[#8B1E2D]">gain</em>, depends on what you <em className="text-[#8B1E2D]">give</em>.
            </h2>
            <div className="mt-8 aspect-[4/3] w-full overflow-hidden">
              <img
                src="/images/join/join-main.jpg"
                alt="Leo Club members"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
          <Stagger className="md:col-span-8 space-y-3" stagger={0.08}>
            {REASONS.map((r, i) => (
              <StaggerItem key={r.title}>
                <div
                  className="group border-t border-black/15 py-8 flex items-start gap-6 transition-all duration-500 hover:border-[#8B1E2D]"
                  data-testid={`reason-${i + 1}`}
                >
                  <div
                    className="leading-none select-none flex-shrink-0 transition-transform duration-500 group-hover:rotate-3"
                    style={{ fontSize: "38px" }}
                    aria-hidden="true"
                  >
                    {r.emoji}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-4 mb-2">
                      <h3 className="font-playfair text-2xl md:text-3xl font-semibold text-[#2C2C2C] leading-tight">
                        {r.title}
                      </h3>
                      <span className="font-inter text-[10px] tracking-[0.3em] uppercase text-[#E76F2E] hidden md:block">
                        0{i + 1}
                      </span>
                    </div>
                    <p className="font-inter text-[#555555] text-base leading-relaxed">{r.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#1A1A1A] text-white relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-100" style={{ mixBlendMode: "overlay" }} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 relative z-10 text-center">
          <Reveal>
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#F4C430] mb-6">
              Ready when you are
            </div>
            <h2 className="font-playfair text-5xl md:text-7xl lg:text-8xl font-semibold leading-[1.02] max-w-5xl mx-auto">
              Step <em className="text-[#F4C430]">forward</em>.
            </h2>
            <p className="mt-8 font-inter text-white/75 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              Apply to join the next cohort, or simply talk to a member to learn more about the journey.
            </p>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              <a
                href="https://forms.gle/4ddjPDUQujJH9bRB6"
                target="_blank"
                rel="noreferrer"
                className="btn-glow inline-flex items-center gap-2 bg-[#F4C430] text-[#1A1A1A] px-8 py-4 rounded-full font-inter text-sm tracking-wide hover:bg-white transition-colors"
                data-testid="join-apply-now"
              >
                Apply Now <ArrowUpRight size={16} />
              </a>
              <a
                href="https://wa.me/918433936670"
                target="_blank"
                rel="noreferrer"
                className="btn-glow inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 rounded-full font-inter text-sm tracking-wide hover:border-[#F4C430] hover:text-[#F4C430] transition-colors"
                data-testid="join-talk-member"
              >
                Talk to a Member
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
